/**
 * What screens can DO (UiActions) and what they can TALK TO (UiServices). Both are implemented/provided by
 * src/app (boot.ts) and consumed with `useActions()` / `useServices()`. This surface is frozen so the UI team and
 * the core team can work in parallel: screens never import src/app, and never write `ui.screen` themselves.
 * The contract itself is platform-neutral (actions-model.ts); this file adds the Preact contexts.
 */
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import { noopActions, type UiActions, type UiServices } from '@hf/client-core/ui-model/actions';

export * from '@hf/client-core/ui-model/actions';

export const ActionsContext = createContext<UiActions>(noopActions);
export const useActions = (): UiActions => useContext(ActionsContext);

/** Context default is null: boot.ts always provides real services (tests provide their own). */
export const ServicesContext = createContext<UiServices | null>(null);
export const useServices = (): UiServices => {
  const s = useContext(ServicesContext);
  if (!s) throw new Error('useServices() outside <ServicesContext.Provider>');
  return s;
};
